<script lang="ts">
  import { useNavigate } from 'svelte-navigator';
  import dayjs from 'dayjs';

  import { DataTable, InlineNotification } from 'carbon-components-svelte';
  import type { DataTableHeader, DataTableRow } from 'carbon-components-svelte/src/DataTable/DataTable.svelte';

  import SaveFeeds from '../SaveFeeds.svelte';
  import EditApprovalModal from '../EditApprovalModal.svelte';
  import PermissionDelayModal from '../PermissionDelayModal.svelte';
  import DataFeedsEntitlementsTableCellRenderer from './DataFeedsEntitlementsTableCellRenderer.svelte';

  import { asBooleanStatusColumn, asOptionalTextColumn } from '../../../../../utils';
  import { getNextWorkdayAfter } from '../../../../../../shared/convert-dates';
  import { DataInventoryServiceIds } from '../../../../../../shared/constants';
  import { appConfig } from '../../../../../stores';
  import { buildObjectRestoreSelection } from '../../../../../domains/entitlements/utils/object-restore-selection';

  import type { EntitlementsController } from '../../../../../domains/entitlements/entitlements-controller';
  import type { EntitlementsModel } from '../../../../../domains/entitlements/entitlements-model';
  import type { EntitlementsParams, FeedEntitlementRow } from '../../../../../domains/entitlements/entitlements-types';
  import type { DraftEntitlementPatch, EditApprovalModalDetail, EditDelayModalDetail, OpenModalDetail } from './entitlements-table-types';

  export let model: EntitlementsModel,
    controller: EntitlementsController,
    data: EntitlementsParams,
    supportsObjectRestore = false,
    submitDisabled = false;

  let selectedRowIds: string[] = [];

  type EditApprovalModalState =
    | {
        open: false;
      }
    | {
        open: true;
        approval: EditApprovalModalDetail['approval'];
        entitlement: EditApprovalModalDetail['entitlement'];
      };

  type EditDelayModalState =
    | {
        open: false;
      }
    | ({
        open: true;
      } & Omit<EditDelayModalDetail, 'name'>);

  let modals: {
    editApproval: EditApprovalModalState;
    editDelay: EditDelayModalState;
  } = {
    editApproval: { open: false },
    editDelay: { open: false },
  };

  const navigate = useNavigate();
  const { awsArbitratedPcapsInventoryS3BucketName } = $appConfig.data;

  const {
    dataFeedMetadata,
    savedEntitlements,
    feedDifference,
    feedIdsRequiringApprovalSet,
    feedInventory,
    savedApprovals,
    savedEntitlementsMetadata,
    validationErrors,
    draftEntitlements,
  } = model;

  const { groupId, serviceId } = data;

  const toEntitlementRow = (row: DataTableRow): FeedEntitlementRow => row as unknown as FeedEntitlementRow;
  const getRowId = (row: DataTableRow): string => toEntitlementRow(row).id;
  const getRowFeedId = (row: DataTableRow): string => toEntitlementRow(row).feedId;

  const tableHeaders = [
    { key: 'name', value: 'Feed Name' },
    { key: 'startDate', value: 'Start Date' },
    { key: 'endDate', value: 'End Date' },
    { key: 'isOngoing', value: 'Ongoing', ...asBooleanStatusColumn },
    { key: 'isDayEntitlement', value: 'Day entitlement', ...asBooleanStatusColumn },
    { key: 'expiryDate', value: 'Expiry date', ...asOptionalTextColumn },
    { key: 'actions', empty: true, sort: false },
  ] as DataTableHeader[];

  const EXPIRY_DATE_DEFAULT_AHEAD_DAYS = 14;

  const SERVICE_TO_PATH_PREFIX = {
    [DataInventoryServiceIds.AWS_ARBITRATED_PCAPS]: `${awsArbitratedPcapsInventoryS3BucketName}/`,
  };

  function closeModal(name: 'editApproval' | 'editDelay') {
    modals = {
      ...modals,
      [name]: { open: false },
    };
  }

  function handleClickForModal(event: CustomEvent<OpenModalDetail>) {
    const { name, ...modalPayload } = event.detail;

    modals = {
      ...modals,
      [name]: {
        open: true,
        ...modalPayload,
      },
    } as typeof modals;
  }

  function handleDeleteRow(event: CustomEvent<{ rowId: string }>) {
    const { rowId } = event.detail;
    model.removeDraftEntitlement(rowId);
    selectedRowIds = selectedRowIds.filter((selectedId) => selectedId !== rowId);
  }

  function handleChangeRow(event: CustomEvent<{ rowId: string; patch: DraftEntitlementPatch }>) {
    const { rowId, patch } = event.detail;
    model.updateDraftEntitlement(rowId, patch);
  }

  function handleToggleIsOngoing(event: CustomEvent<{ rowId: string }>) {
    const { rowId } = event.detail;
    const row = $draftEntitlements.find((feed) => feed.id === rowId);
    if (!row || row.isDayEntitlement) {
      return;
    }

    if (row.isOngoing) {
      model.updateDraftEntitlement(rowId, { isOngoing: false, onGoingMaxDate: null });
      return;
    }

    model.updateDraftEntitlement(rowId, { isOngoing: true });
  }

  function handleToggleDayEntitlement(event: CustomEvent<{ rowId: string }>) {
    const { rowId } = event.detail;
    const row = $draftEntitlements.find((feed) => feed.id === rowId);
    if (!row) {
      return;
    }

    const feed = $feedInventory.find(({ id }) => id === row.feedId);
    const today = dayjs().format('YYYY-MM-DD');

    if (row.isDayEntitlement) {
      model.updateDraftEntitlement(rowId, {
        isDayEntitlement: false,
        expiryDate: null,
        startDate: feed?.availabilityStartMonth ?? today.slice(0, 7),
        endDate: feed?.availabilityEndMonth ?? today.slice(0, 7),
      });
      return;
    }

    model.updateDraftEntitlement(rowId, {
      isDayEntitlement: true,
      expiryDate: getNextWorkdayAfter(EXPIRY_DATE_DEFAULT_AHEAD_DAYS),
      isOngoing: false,
      onGoingMaxDate: null,
      startDate: feed?.availabilityStartDate ?? today,
      endDate: feed?.availabilityStartDate ?? today,
    });
  }

  function sendToObjectRestore() {
    const selectedEntitlements = buildObjectRestoreSelection({
      selectedRowIds,
      draftEntitlements: $draftEntitlements,
      feedInventory: $feedInventory,
      serviceId,
      serviceToPathPrefix: SERVICE_TO_PATH_PREFIX,
    });

    navigate('/object-restore-request/create', {
      state: {
        selectedEntitlements,
      },
    });
  }

  $: savedEntitlementIdsSet = new Set<string>((($savedEntitlements ?? []) as FeedEntitlementRow[]).map(({ id }) => id));
</script>

<div class="data-feed-entitlements-table-wrapper" class:object-restore-support={supportsObjectRestore}>
  <h3 class="section-heading h5">Entitlements</h3>
  <div class="entitlements-table" class:selectable={supportsObjectRestore}>
    {#if $draftEntitlements.length}
      <DataTable
        class="mst-table--lock-end"
        bind:selectedRowIds
        selectable={supportsObjectRestore}
        batchSelection={supportsObjectRestore}
        headers={tableHeaders}
        rows={$draftEntitlements}
        sortable
        size="short"
      >
        <span slot="cell" let:cell let:row>
          <DataFeedsEntitlementsTableCellRenderer
            row={toEntitlementRow(row)}
            {cell}
            {serviceId}
            {savedEntitlementIdsSet}
            feedIdsRequiringApprovalSet={$feedIdsRequiringApprovalSet}
            approval={$savedApprovals[getRowId(row)]}
            entitlementMetadata={$savedEntitlementsMetadata[getRowId(row)]}
            rowValidationErrors={$validationErrors[getRowId(row)] ?? {}}
            feedMetadata={$dataFeedMetadata[getRowFeedId(row)]}
            on:open-modal={handleClickForModal}
            on:delete-row={handleDeleteRow}
            on:change-row={handleChangeRow}
            on:toggle-is-ongoing={handleToggleIsOngoing}
            on:toggle-day-entitlement={handleToggleDayEntitlement}
          />
        </span>
      </DataTable>
    {:else}
      <InlineNotification subtitle="No entitlements selected yet" kind="info" hideCloseButton lowContrast />
    {/if}
  </div>
</div>

<SaveFeeds
  {submitDisabled}
  {selectedRowIds}
  {supportsObjectRestore}
  feedIdsRequiringApprovalSet={$feedIdsRequiringApprovalSet}
  feedDifference={$feedDifference}
  validationErrors={$validationErrors}
  draftEntitlements={$draftEntitlements}
  onSubmitChanges={controller.saveChanges}
  on:object-restore={sendToObjectRestore}
/>

{#if modals.editApproval.open}
  <EditApprovalModal
    open
    {groupId}
    approval={modals.editApproval.approval ?? {}}
    entitlement={modals.editApproval.entitlement}
    onSaveApproval={controller.saveApproval}
    on:close={() => closeModal('editApproval')}
  />
{/if}

{#if modals.editDelay.open}
  <PermissionDelayModal
    {serviceId}
    {...modals.editDelay}
    feedMetadata={modals.editDelay.feedMetadata ?? {}}
    entitlementMetadata={modals.editDelay.entitlementMetadata ?? {}}
    onSaveMetadata={controller.saveMetadata}
    onDeleteMetadata={controller.deleteMetadata}
    on:close={() => closeModal('editDelay')}
  />
{/if}

<style>
  .data-feed-entitlements-table-wrapper {
    margin-top: 2rem;
  }

  .data-feed-entitlements-table-wrapper :global(tbody td) {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    font-size: 0.8rem !important;
  }

  .data-feed-entitlements-table-wrapper :global(input::placeholder) {
    font-size: 0.7rem !important;
  }

  .data-feed-entitlements-table-wrapper :global(.bx--text-input) {
    font-size: 0.8rem !important;
    padding: 0 0.5rem;
    width: 85px;
  }

  .data-feed-entitlements-table-wrapper :global(.bx--text-input__invalid-icon) {
    display: none;
  }

  .data-feed-entitlements-table-wrapper :global(thead th .bx--table-sort),
  .data-feed-entitlements-table-wrapper :global(thead th.bx--table-column-checkbox) {
    padding-left: 0.5rem;
  }

  .data-feed-entitlements-table-wrapper.object-restore-support :global(td:first-child),
  .data-feed-entitlements-table-wrapper.object-restore-support :global(th:first-child) {
    min-width: 30px !important;
  }

  /* name */
  .data-feed-entitlements-table-wrapper:not(.object-restore-support) :global(td:first-child),
  .data-feed-entitlements-table-wrapper.object-restore-support :global(td:nth-child(2)) {
    width: 100%;
  }

  /* dates */
  .data-feed-entitlements-table-wrapper:not(.object-restore-support) :global(td:nth-child(2)),
  .data-feed-entitlements-table-wrapper :global(td:nth-child(3)),
  .data-feed-entitlements-table-wrapper.object-restore-support :global(td:nth-child(4)) {
    min-width: 107px;
  }

  /* onGoing, expiry date */
  .data-feed-entitlements-table-wrapper:not(.object-restore-support) :global(td:nth-child(4)),
  .data-feed-entitlements-table-wrapper.object-restore-support :global(td:nth-child(5)) {
    min-width: 129px;
  }

  /* day entitlement */
  .data-feed-entitlements-table-wrapper:not(.object-restore-support) :global(td:nth-child(5)),
  .data-feed-entitlements-table-wrapper.object-restore-support :global(td:nth-child(6)) {
    min-width: 145px;
  }

  /* expiry date */
  .data-feed-entitlements-table-wrapper:not(.object-restore-support) :global(td:nth-child(6)),
  .data-feed-entitlements-table-wrapper.object-restore-support :global(td:nth-child(7)) {
    min-width: 115px;
  }

  /* actions */
  .data-feed-entitlements-table-wrapper :global(td:last-child) {
    min-width: 79px;
  }

  .data-feed-entitlements-table-wrapper :global(label.bx--visually-hidden) {
    display: none;
  }

  .section-heading {
    margin: 0 0 0.5rem;
  }

  @supports (-webkit-touch-callout: none) {
    @media (hover: none) and (pointer: coarse) {
      .data-feed-entitlements-table-wrapper :global(.bx--text-input) {
        font-size: 16px !important;
      }
    }
  }
</style>
